import { Link } from "wouter";
import { NavBar, Footer } from "@/pages/homepage";
import { TorkSeoPage, TorkVsl, useTorkSeoHead, type Page } from "@/components/tork/TorkBlocks";
import { getPage } from "@/lib/tork/pages";
import { NICHES } from "@/lib/tork/niches";
import { buildNichePage } from "@/lib/tork/nicheTemplate";
import { ALL_LISTICLES, getListicle, CATEGORY_LISTICLE_SLUGS } from "@/lib/tork/listicles";
import { buildListiclePage } from "@/lib/tork/listicleTemplate";
import { TRIAL_HREF, TRIAL_LABEL, TRUST_ITEMS } from "@/lib/tork/cta";
import { TestimonialsSection, SectionCTA } from "@/components/marketing/social-proof";
import NotFound from "@/pages/not-found";

// Shared chrome for every public SEO page: site NavBar/Footer + fixed-nav offset.
function SeoShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      <NavBar />
      {/* offset the fixed NavBar (h-40 = 10rem) */}
      <div style={{ paddingTop: "10rem" }}>{children}</div>
      {/* Real-results proof + a $1-trial CTA on every public SEO page. */}
      <TestimonialsSection showStats={false} showExplainers={false} />
      <SectionCTA testid="cta-seo" body="$1 for 3 days, then your plan price. 10x the value or cancel in one click." />
      <Footer />
    </div>
  );
}

// Public SEO landing pages for logged-out visitors / crawlers. TorkSeoPage
// renders the page body from the final copy in lib/tork/pages.tsx.
export default function SeoPage({ slug }: { slug: string }) {
  const page = getPage(slug);
  useTorkSeoHead(page);
  return (
    <SeoShell>
      <TorkSeoPage page={page} />
    </SeoShell>
  );
}

// Programmatic per-industry page: /for/<niche>. The wrapper validates the slug
// before NicheView (which calls hooks) mounts, keeping hook order stable.
export function SeoNichePage({ slug }: { slug: string }) {
  const niche = NICHES.find((n) => n.slug === slug);
  if (!niche) return <NotFound />;
  return <NicheView niche={niche} />;
}

function NicheView({ niche }: { niche: (typeof NICHES)[number] }) {
  const page = buildNichePage(niche);
  useTorkSeoHead(page);
  return (
    <SeoShell>
      <TorkSeoPage page={page} />
    </SeoShell>
  );
}

// Industry hub: /for — groups every niche by category, preserving first-seen order.
const HUB_HEAD = {
  slug: "/for",
  title: "Local SEO Software by Industry — Tork for Your Business",
  description:
    "AI local SEO software tailored to your industry. Get found on Google Maps and in AI search (ChatGPT, Google AI Overviews) — for lawyers, dentists, doctors, contractors, salons, and 100+ more. Start for $1.",
  jsonLd: [],
  blocks: [],
} as unknown as Page;

function groupedNiches() {
  const order: string[] = [];
  const map: Record<string, typeof NICHES> = {};
  for (const n of NICHES) {
    if (!map[n.category]) {
      map[n.category] = [];
      order.push(n.category);
    }
    map[n.category].push(n);
  }
  return order.map((cat) => ({ cat, items: map[cat] }));
}

export function SeoForHub() {
  useTorkSeoHead(HUB_HEAD);
  const groups = groupedNiches();
  return (
    <SeoShell>
      <main className="tork-page">
        <header className="tork-hero">
          <h1 className="tork-h1">Local SEO Software for Your Industry</h1>
          <p className="tork-subhead">
            Tork is Done-With-You AI local SEO tuned to how your specific business gets chosen — on Google
            Maps and in AI search like ChatGPT and Google AI Overviews. Pick your industry below. It's the
            system that's helped 5,000+ businesses rank.
          </p>
          <div className="tork-cta-row">
            <a href={TRIAL_HREF} className="tork-btn" data-testid="link-hub-trial-top">{TRIAL_LABEL}</a>
          </div>
        </header>

        <TorkVsl />

        <div className="tork-trustbar">
          {TRUST_ITEMS.map((t, i) => (
            <span key={i} className="tork-trustbar-item">{t}</span>
          ))}
        </div>

        {groups.map((g) => (
          <section key={g.cat} className="tork-section">
            <h2 className="tork-h2">{g.cat}</h2>
            <div className="tork-cardgrid">
              {g.items.map((n) => (
                <Link key={n.slug} href={"/for/" + n.slug} className="tork-card" data-testid={`link-niche-${n.slug}`}>
                  <span className="tork-card-title">{n.name}</span>
                  <span className="tork-card-blurb">Get your {n.singular} found on Maps &amp; AI search →</span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <section className="tork-ctaband">
          <h2 className="tork-h2">Don't see your industry?</h2>
          <p className="tork-p">Tork works for any local business. Start your $1 trial and we'll tailor it to you.</p>
          <a href={TRIAL_HREF} className="tork-btn" data-testid="link-hub-trial-bottom">{TRIAL_LABEL}</a>
        </section>
      </main>
    </SeoShell>
  );
}

// Programmatic listicle page: /best/<topic>. Validates the slug before
// ListicleView (which calls hooks) mounts, keeping hook order stable.
export function SeoBestPage({ slug }: { slug: string }) {
  const listicle = getListicle(slug);
  if (!listicle) return <NotFound />;
  return <ListicleView slug={slug} />;
}

function ListicleView({ slug }: { slug: string }) {
  const page = buildListiclePage(getListicle(slug)!);
  useTorkSeoHead(page);
  return (
    <SeoShell>
      <TorkSeoPage page={page} />
    </SeoShell>
  );
}

// Best-of hub: /best — category roundups first, then industry roundups.
const BEST_HEAD = {
  slug: "/best",
  title: "Best Local SEO Software — Buyer's Guides & Rankings | Tork",
  description:
    "Independent best-of rankings for local SEO software — by category and by industry. See which tools rank you on Google Maps and in AI search, and why.",
  jsonLd: [],
  blocks: [],
} as unknown as Page;

export function SeoBestHub() {
  useTorkSeoHead(BEST_HEAD);
  const categorySet = new Set(CATEGORY_LISTICLE_SLUGS);
  const category = ALL_LISTICLES.filter((l) => categorySet.has(l.slug));
  const industry = ALL_LISTICLES.filter((l) => !categorySet.has(l.slug));
  return (
    <SeoShell>
      <main className="tork-page">
        <header className="tork-hero">
          <h1 className="tork-h1">Best Local SEO Software — Rankings &amp; Buyer's Guides</h1>
          <p className="tork-subhead">
            Independent, honestly-ranked roundups of the best local SEO software — by category and by
            industry. Each guide compares real tools on what actually moves Google Maps and AI-search visibility.
          </p>
          <div className="tork-cta-row">
            <a href={TRIAL_HREF} className="tork-btn" data-testid="link-best-trial-top">{TRIAL_LABEL}</a>
          </div>
        </header>

        <TorkVsl />

        <div className="tork-trustbar">
          {TRUST_ITEMS.map((t, i) => (
            <span key={i} className="tork-trustbar-item">{t}</span>
          ))}
        </div>

        <section className="tork-section">
          <h2 className="tork-h2">By category</h2>
          <div className="tork-cardgrid">
            {category.map((l) => (
              <Link key={l.slug} href={"/best/" + l.slug} className="tork-card" data-testid={`link-best-${l.slug}`}>
                <span className="tork-card-title">{l.breadcrumbName}</span>
                <span className="tork-card-blurb">Ranked &amp; compared for {l.query.replace(/^best /, "")} →</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="tork-section">
          <h2 className="tork-h2">By industry</h2>
          <div className="tork-cardgrid">
            {industry.map((l) => (
              <Link key={l.slug} href={"/best/" + l.slug} className="tork-card" data-testid={`link-best-${l.slug}`}>
                <span className="tork-card-title">{l.breadcrumbName}</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </SeoShell>
  );
}
